import React from 'react';
import { Divider, Image } from 'antd';
import { Box, Flex, Link } from 'theme-ui';
import { Popover } from 'components/common';
import { useGlobalContext } from 'state/GlobalContext';
import { ChargeDetails } from 'page-components/ChargeDetails';
import { ActionButton } from 'page-components/ActionButton';
import { Reservation } from 'models';
import { SiteInfo } from 'interfaces/SiteInfo';
import { ParkPayload } from 'services/payloads/ParkPayload';
import { stateOptions } from 'services/GeoData';

type Props = {
  formValues: Reservation;
  onBookNow: () => void;
  canBook: boolean;
  isLongTerm?: boolean;
}
const SiteDetailSection: React.FC<Props> = ({ formValues, onBookNow, canBook, isLongTerm }) => {
  const [{ state }] = useGlobalContext();
  const park = state.selectedPark;
  const siteType = state.siteType;
  const rates = state.siteType?.rates || [];
  const siteInfo: SiteInfo = {
    parkName: park?.name,
    parkAddress1: park?.address,
    parkAddress2: getAddressDetails(park),
    siteTypeName: siteType?.name,
    siteTypePhotoUrl: siteType?.primaryPhotoUrl,
    startingRate: getStartingRate(rates),
  }

  const SiteContent = (
    <Box sx={{maxWidth: 320}}>
      {siteType?.description}
    </Box>
  )

  return (
    <>
      <Flex sx={{gap: 16}}>
      <ImageBox src={siteInfo?.siteTypePhotoUrl} />
        {siteInfo && (
          <Box sx={{flex: 1}}>
            <Text bold>{siteInfo.parkName}</Text>
            <Text sm>{siteInfo.parkAddress1}</Text>
            <Text sm>{siteInfo.parkAddress2}</Text>
            <Text></Text>
            {siteType && (
              <>
                <Text bold>{siteInfo.siteTypeName}</Text>
                <Flex sx={{ fontSize: 11, fontWeight: 500, justifyContent: 'space-between' }}>
                  <Text>Starting at ${siteInfo.startingRate} / night</Text>
                  <Popover content={SiteContent}>
                    <Link>View Details</Link>
                  </Popover>
                </Flex>
              </>
            )}
          </Box>
        )}
      </Flex>
      <Divider />
      <ChargeDetails title="Price details" quote={state.quote} isLongTerm={isLongTerm} phone={park?.phone} />
      <ActionButton onClick={onBookNow} disabled={!canBook} sx={{mt: 4}}>Book Now</ActionButton>
    </>
  );
};

type TextProps = {
  sm?: boolean, md?: boolean, lg?: boolean,
  bold?: boolean
}
const Text: React.FC<TextProps> = ({ sm, md, lg, bold, ...props }) => {
  const size = (sm && 11) || (md && 14) || (lg && 16);
  const weight = bold ? 'bold' : undefined;

  return (
   <Box {...props} sx={{fontSize: size, fontWeight: weight, lineHeight: 1.25, minHeight: '1.25em' }} />
  )
}

const ImageBox = ({ src }) => (
  <Box sx={{width: 110, height: 100}}>
    <Image src={src || '/assets/tent.png'} preview={false} width="auto" height="100%"
      style={{ borderRadius: 8, objectFit: 'cover' }}
    />
  </Box>
)

function getStartingRate(rates: any) {
  const minRates = rates.map(rate => {
    const amountFields = Object.keys(rate).filter(key => key.match(/Amount$/));
    const amounts = amountFields.map(field => rate[field]).filter(val => val > 0);
    const minAmount = Math.min(...amounts);
    return minAmount;
  });

  return Math.min(...minRates);
}

function getAddressDetails(park: ParkPayload) {
  const states = stateOptions(park?.country);
  const stateName = states.find(x => x.value === park?.state)?.label;
  const result = `${park?.city}, ${stateName}, ${park?.postalCode}`;
  return result;
}

export default SiteDetailSection;
